import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subscription, finalize, forkJoin } from 'rxjs';
import { MyOskLectureGroupMemberService } from '../../../../api/api-services/my-osk-lecture-group-member.service';
import { MyOskLectureGroupMembersService } from '../../../../api/api-services/my-osk-lecture-group-members.service';
import { MyOskStudentsService } from '../../../../api/api-services/my-osk-students.service';
import { IMyOskLectureGroupVm } from '../../../../api/my-osk-lecture-group-vm.model';
import { eModalRoles } from '../../../shared/models/modals';
import { LECTURE_GROUP_STUDENTS_PAGE_SIZE, StudentOption } from '../lecture-group-form/lecture-group-form.component';

/** The "+" action of a group row: picks students of the OSK who are not in the group yet. */
@Component({
  selector: 'app-lecture-group-add-students',
  templateUrl: './lecture-group-add-students.component.html',
  styleUrl: './lecture-group-add-students.component.scss',
})
export class LectureGroupAddStudentsComponent implements OnInit, OnDestroy {
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly dialogConfig = inject(DynamicDialogConfig);
  private readonly messageService = inject(MessageService);
  private readonly myOskStudentsService = inject(MyOskStudentsService);
  private readonly myOskLectureGroupMembersService = inject(MyOskLectureGroupMembersService);
  private readonly myOskLectureGroupMemberService = inject(MyOskLectureGroupMemberService);

  readonly group = this.dialogConfig.data.group as IMyOskLectureGroupVm;

  studentOptions: StudentOption[] = [];
  selectedStudentGuids: string[] = [];
  loadingOptions = true;
  saving = false;

  private readonly subscriptions = new Subscription();

  ngOnInit(): void {
    this.subscriptions.add(
      forkJoin({
        students: this.myOskStudentsService.get(false, '', 'created', 1, LECTURE_GROUP_STUDENTS_PAGE_SIZE),
        members: this.myOskLectureGroupMembersService.get(this.group.guid),
      })
        .pipe(finalize(() => (this.loadingOptions = false)))
        .subscribe({
          next: ({ students, members }) => {
            const memberGuids = new Set(members.map((member) => member.guid));
            this.studentOptions = (students.data ?? [])
              .filter((student) => !memberGuids.has(student.guid))
              .map((student) => ({
                label: student.email,
                value: student.guid,
                name: (student.displayName ?? '').trim(),
              }));
          },
          error: () =>
            this.messageService.add({
              severity: 'error',
              summary: 'Błąd',
              detail: 'Nie udało się pobrać listy kursantów.',
            }),
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  closeDialog(status: eModalRoles = eModalRoles.CLOSE): void {
    this.dialogRef.close(status);
  }

  save(): void {
    if (this.saving || this.selectedStudentGuids.length === 0) {
      return;
    }
    this.saving = true;
    this.subscriptions.add(
      forkJoin(
        this.selectedStudentGuids.map((studentGuid) =>
          this.myOskLectureGroupMemberService.post({ lectureGroupGuid: this.group.guid, studentGuid })
        )
      )
        .pipe(finalize(() => (this.saving = false)))
        .subscribe({
          next: (added) => {
            this.messageService.add({
              severity: 'success',
              summary: 'Sukces',
              detail: `Dodano kursantów do grupy: ${added.length}.`,
            });
            this.closeDialog(eModalRoles.SUCCESS);
          },
          error: (error: HttpErrorResponse) => {
            const apiMessage =
              typeof error.error === 'string' ? error.error : error.error?.message ?? error.error?.title;
            this.messageService.add({
              severity: 'error',
              summary: 'Błąd',
              detail: apiMessage?.includes('archived group')
                ? 'Nie można dodawać kursantów do zarchiwizowanej grupy.'
                : 'Nie udało się dodać wszystkich wybranych kursantów.',
            });
            // Some of the students may already have been added - the list reloads either way.
            this.closeDialog(eModalRoles.REFRESH);
          },
        })
    );
  }
}
